import React, { useState, useEffect } from 'react';
import api, { uploadFile } from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit3, Trash2, Tag, X, Users, Upload, Globe, Instagram, Youtube, Facebook, Music2, Image } from 'lucide-react';

// ─── Modal creazione/modifica label (stessi campi del frontend utente) ────────
function AdminLabelModal({ label, userId, onClose, onSaved }) {
  const [form, setForm] = useState(label
    ? { name: label.name || '', description: label.description || '', logo_path: label.logo_path || '',
        link_website: label.link_website || '', link_instagram: label.link_instagram || '',
        link_facebook: label.link_facebook || '', link_youtube: label.link_youtube || '', link_tiktok: label.link_tiktok || '' }
    : { name: '', description: '', logo_path: '', link_website: '', link_instagram: '', link_facebook: '', link_youtube: '', link_tiktok: '' }
  );
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(label?.logo_path ? `/uploads/artwork/${userId}/${label.logo_path}` : null);
  const [uploading, setUploading] = useState(false);

  const handleLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const { data } = await uploadFile(`/uploads/artwork?for_user=${userId}`, f, 'artwork');
      setForm(p => ({ ...p, logo_path: data.file.filename }));
      setLogoPreview(URL.createObjectURL(f));
      toast.success('Logo caricato');
    } catch (err) { toast.error('Errore upload'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error('Nome label obbligatorio');
    setSaving(true);
    try {
      if (label?.id) {
        await api.put(`/labels/admin/${label.id}`, form);
        toast.success('Label aggiornata!');
      } else {
        await api.post(`/labels/admin/for-user/${userId}`, form);
        toast.success('Label creata!');
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg text-white">{label?.id ? 'Modifica Label' : 'Nuova Label'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded text-gray-400"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl bg-gray-700 border-2 border-dashed border-gray-600 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {uploading
                ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400" />
                : logoPreview
                  ? <img src={logoPreview} alt="" className="w-full h-full object-cover" />
                  : <Image size={24} className="text-gray-500" />}
            </div>
            <div>
              <input type="file" accept="image/*" className="hidden" id="adm-label-logo" onChange={handleLogo} />
              <label htmlFor="adm-label-logo"
                className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg cursor-pointer hover:bg-gray-600 inline-flex items-center gap-1.5 text-xs">
                <Upload size={12} />Logo Label
              </label>
              <p className="text-[10px] text-gray-500 mt-1">JPG/PNG, quadrata, 500×500px</p>
              {logoPreview && (
                <button onClick={() => { setForm(p => ({ ...p, logo_path: '' })); setLogoPreview(null); }}
                  className="text-[10px] text-red-400 hover:underline mt-1">Rimuovi logo</button>
              )}
            </div>
          </div>

          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Nome Label *</label>
            <input className="input-field-dark" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus />
          </div>

          {/* Descrizione */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Descrizione</label>
            <textarea className="input-field-dark h-20 resize-y" value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Descrivi la label..." />
          </div>

          {/* Social & Link */}
          <div className="border-t border-gray-700 pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-3">Link & Social</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Globe size={14} className="text-gray-500 flex-shrink-0" />
                <input className="input-field-dark" value={form.link_website}
                  onChange={e => setForm(p => ({ ...p, link_website: e.target.value }))} placeholder="https://tuosito.com" />
              </div>
              <div className="flex items-center gap-2">
                <Instagram size={14} className="text-gray-500 flex-shrink-0" />
                <input className="input-field-dark" value={form.link_instagram}
                  onChange={e => setForm(p => ({ ...p, link_instagram: e.target.value }))} placeholder="https://instagram.com/label" />
              </div>
              <div className="flex items-center gap-2">
                <Facebook size={14} className="text-gray-500 flex-shrink-0" />
                <input className="input-field-dark" value={form.link_facebook}
                  onChange={e => setForm(p => ({ ...p, link_facebook: e.target.value }))} placeholder="https://facebook.com/label" />
              </div>
              <div className="flex items-center gap-2">
                <Youtube size={14} className="text-gray-500 flex-shrink-0" />
                <input className="input-field-dark" value={form.link_youtube}
                  onChange={e => setForm(p => ({ ...p, link_youtube: e.target.value }))} placeholder="https://youtube.com/@label" />
              </div>
              <div className="flex items-center gap-2">
                <Music2 size={14} className="text-gray-500 flex-shrink-0" />
                <input className="input-field-dark" value={form.link_tiktok}
                  onChange={e => setForm(p => ({ ...p, link_tiktok: e.target.value }))} placeholder="https://tiktok.com/@label" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">Annulla</button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-2.5 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-400 disabled:opacity-50 text-sm">
            {saving ? 'Salvataggio...' : label?.id ? 'Salva Modifiche' : 'Crea Label'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function AdminLabelsPage() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, label: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/users?limit=500');
        setUsers((data.users || []).filter(u => u.role !== 'admin'));
      } catch (e) {}
    })();
  }, []);

  const loadLabels = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try { const { data } = await api.get(`/labels/admin/by-user/${selectedUser}`); setLabels(data.labels || []); }
    catch (e) { setLabels([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (selectedUser) loadLabels(); else setLabels([]); }, [selectedUser]);

  const deleteLabel = async (id, name) => {
    if (!window.confirm(`Eliminare la label "${name}"?`)) return;
    try {
      await api.delete(`/labels/admin/${id}`);
      toast.success('Label eliminata');
      loadLabels();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore eliminazione'); }
  };

  const selectedUserObj = users.find(u => String(u.id) === selectedUser);

  return (
    <div>
      {modal.open && (
        <AdminLabelModal
          label={modal.label}
          userId={selectedUser}
          onClose={() => setModal({ open: false, label: null })}
          onSaved={() => { setModal({ open: false, label: null }); loadLabels(); }}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Gestione Label</h1>
        {selectedUser && (
          <button onClick={() => setModal({ open: true, label: null })}
            className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-400 inline-flex items-center gap-2 text-sm">
            <Plus size={16} />Nuova Label
          </button>
        )}
      </div>

      <div className="mb-6">
        <select className="input-field-dark w-full" value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
          <option value="">Seleziona un utente...</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}
        </select>
      </div>

      {!selectedUser ? (
        <div className="card-dark p-12 text-center text-gray-500">
          <Users size={48} className="mx-auto mb-4 opacity-30" />
          <p>Seleziona un utente per vedere le sue label</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400" /></div>
      ) : (
        <div>
          <p className="text-sm text-gray-400 mb-4">
            Label di <strong className="text-white">{selectedUserObj?.first_name} {selectedUserObj?.last_name}</strong> ({labels.length})
          </p>

          {labels.length === 0 ? (
            <div className="card-dark p-8 text-center text-gray-500">
              <Tag size={32} className="mx-auto mb-3 opacity-30" />
              <p>Nessuna label per questo utente</p>
              <button onClick={() => setModal({ open: true, label: null })}
                className="mt-3 text-xs text-yellow-400 hover:underline">+ Crea la prima label</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {labels.map(l => (
                <div key={l.id} className="card-dark p-5 group hover:border-gray-600 transition">
                  <div className="flex items-start gap-3">
                    {/* Logo */}
                    {l.logo_path
                      ? <img src={`/uploads/artwork/${l.user_id}/${l.logo_path}`} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                      : <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                          <Tag size={18} className="text-yellow-400" />
                        </div>}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate">{l.name}</h3>
                      {l.description && <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{l.description}</p>}
                    </div>
                  </div>

                  {/* Social badges */}
                  {(l.link_website || l.link_instagram || l.link_facebook || l.link_youtube || l.link_tiktok) && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {l.link_website && <a href={l.link_website} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-white"><Globe size={12} /></a>}
                      {l.link_instagram && <a href={l.link_instagram} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-pink-400"><Instagram size={12} /></a>}
                      {l.link_facebook && <a href={l.link_facebook} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-blue-400"><Facebook size={12} /></a>}
                      {l.link_youtube && <a href={l.link_youtube} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-red-400"><Youtube size={12} /></a>}
                      {l.link_tiktok && <a href={l.link_tiktok} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-white"><Music2 size={12} /></a>}
                    </div>
                  )}

                  {/* Azioni */}
                  <div className="flex justify-end gap-3 mt-3 pt-3 border-t border-gray-700">
                    <button onClick={() => setModal({ open: true, label: l })}
                      className="text-xs text-yellow-400 hover:underline inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      <Edit3 size={12} />Modifica
                    </button>
                    <button onClick={() => deleteLabel(l.id, l.name)}
                      className="text-xs text-red-400 hover:underline inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      <Trash2 size={12} />Elimina
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
